import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createOrUpdatePersonalizedLetter } from 'api/engage';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import * as Dialog from 'components/dialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface PersonalizedLetterUnsavedChangesDialogProps extends Dialog.DialogProps {}

const PersonalizedLetterUnsavedChangesDialog = (
    props: PersonalizedLetterUnsavedChangesDialogProps,
) => {
    const { children, ...rest } = props;
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const {
        editorInstance,
        setCurrentPersonalizedLetterContact,
        setHasDiffFromMasterLetter,
        setIsUnsavedChangesDialogOpen,
        setNextPersonalizedLetterContact,
        currentPersonalizedLetterContact,
        nextPersonalizedLetterContact,
    } = usePersonalizedLettersStore();

    const { mutate: updatePersonalizedLetter } = useMutation({
        mutationFn: () => {
            const body = editorInstance?.getContent();

            return createOrUpdatePersonalizedLetter(
                accountId,
                letter.id,
                currentPersonalizedLetterContact?.id ?? 0,
                {
                    body: body ?? '',
                },
            );
        },
        onSuccess: () => {
            success('Saved personalized letter!');
            setHasDiffFromMasterLetter(false);
            setIsUnsavedChangesDialogOpen(false);
            setCurrentPersonalizedLetterContact(nextPersonalizedLetterContact);
            setNextPersonalizedLetterContact(undefined);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to save personalized letter'),
    });

    const discardChanges = () => {
        setIsUnsavedChangesDialogOpen(false);
        setCurrentPersonalizedLetterContact(nextPersonalizedLetterContact);
        setNextPersonalizedLetterContact(undefined);
    };

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <div className="flex flex-col gap-4">
                        <Dialog.Title>Unsaved changes</Dialog.Title>
                        <Dialog.Description>
                            You have unsaved changes that will be deleted if you move on to the next
                            recipient.
                        </Dialog.Description>
                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                color="neutral"
                                className="flex-1"
                                onClick={() => discardChanges()}
                            >
                                Discard changes
                            </Button>
                            <Button
                                variant="solid"
                                className="flex-1"
                                onClick={() => updatePersonalizedLetter()}
                            >
                                Save changes
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const Root = PersonalizedLetterUnsavedChangesDialog;
const PersonalizedLetterUnsavedChangesDialogTrigger = Dialog.Trigger;
const Trigger = PersonalizedLetterUnsavedChangesDialogTrigger;

export {
    Root,
    Trigger,
    PersonalizedLetterUnsavedChangesDialog,
    PersonalizedLetterUnsavedChangesDialogTrigger,
};
